import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from '@tanstack/react-router';
import { connect, ConnectedProps } from 'react-redux';
import { TFunction } from 'react-i18next';
import {
  ConfigService, withInject, Pagination as PaginationManager, QueryParams,
} from '@piwikpro/platform';
import { buildPaginatedPath } from '@piwikpro/router-crate';
import { ListPreference } from '@piwikpro/user-preferences';
import {
  Text,
  StyledContainer,
  Pagination,
  Tag,
  Layout,
  Stack,
  View,
  withTranslation,
  Button,
  List,
  Spinner,
  SplashScreen,
  Icon,
  Trans,
  formatDate,
} from '@piwikpro/ui-components';
import moment from 'moment';
import { ProductAnalytics } from '@piwikpro/product-analytics';
import { BillingService } from '../../BillingService';
import PaymentBadge from '../PaymentBadge/PaymentBadge';
import { MarketingCratePaymentsState, Transaction } from '../../redux';

const PRODUCT_ANALYTICS_ACTION_TYPES = {
  showBillingAndPayments: '@platform/command/payments/showBillingAndPayments',
  downloadInvoice: '@platform/command/payments/downloadInvoice',
  downloadInvoiceResult: '@platform/command/payments/downloadInvoiceResult',
};

const mapStateToProps = (state: MarketingCratePaymentsState) => ({
  isBillingLoading: state.payments.billing.isLoading,
  isSubscriptionLoading: state.payments.subscription.isLoading,
  transactionListFailed: state.payments.billing.failed,
  failed: state.payments.subscription.failed,
  hasPaddleSubscription: Boolean(state.payments.subscription.details),
  transactions: state.payments.billing.details,
  total: state.payments.billing.details?.length || 0,
});

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  billingService: BillingService
  config: ConfigService
  Title: any
  userPreferences: any
  productAnalytics: ProductAnalytics
  ScheduledActionInformation: React.ComponentType<Record<string, never>>
}

interface Props extends ReduxProps, InjectedProps {
  t: TFunction
}

function downloadURI(uri: string, name: string) {
  const link = document.createElement('a');
  link.download = name;
  link.href = uri;
  link.target = '_parent';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

const getTrafficLimitParsed = (traffic: number) => {
  if (traffic >= 1000000) {
    return `${traffic / 1000000}M`;
  }
  if (traffic >= 1000) {
    return `${traffic / 1000}K`;
  }
  return traffic.toString();
};

const getTierName = (transaction: Transaction) => {
  if (!transaction.traffic_limit || !transaction.domains_limit) {
    return transaction.tier_name;
  }

  const trafficLimit = getTrafficLimitParsed(transaction.traffic_limit);
  const domainsLimit = transaction.domains_limit;

  return (
    <Trans
      i18nKey="paddle.billing.transaction.transaction.tier-name"
      values={{
        count: domainsLimit,
        trafficLimit,
      }}
    />
  );
};

const Billing = ({
  transactions,
  isBillingLoading,
  isSubscriptionLoading,
  failed,
  config,
  billingService,
  Title,
  total,
  userPreferences,
  hasPaddleSubscription,
  t,
  productAnalytics,
  transactionListFailed,
  ScheduledActionInformation,
}: Props) => {
  const [dateSortOrder, setDateSortOrder] = useState<'asc' | 'dsc'>();
  const [sortedAndSplittedTransactions, setSortedAndSplittedTransactions] = useState(transactions);
  const { pathname } = useLocation();
  const routeParams = useParams({ strict: false });
  const navigate = useNavigate();
  const pagination = PaginationManager.fromQueryParams(
    transactions || [],
    total,
    routeParams as QueryParams,
  );

  const fetchTransactions = async () => {
    try {
      await billingService.getTransactions();
      await billingService.getCurrentSubscriptionTier();
      await billingService.getTiers();
    // eslint-disable-next-line no-empty
    } catch {}
  };

  useEffect(() => {
    if (transactions?.length) {
      const transactionsToSort = [...transactions].filter(transaction => !!transaction.invoice_number || transaction.status === 'past_due');

      transactionsToSort?.sort((a, b) => {
        if (dateSortOrder === 'asc') {
          return moment(a.billing_date).valueOf() - moment(b.billing_date).valueOf();
        }

        return moment(b.billing_date).valueOf() - moment(a.billing_date).valueOf();
      });
      const sortedAndPaginatedTransactions = transactionsToSort.slice(
        pagination.offset,
        pagination.offset + pagination.getLimit(),
      );
      setSortedAndSplittedTransactions(sortedAndPaginatedTransactions);
    }
  }, [dateSortOrder, routeParams.page, routeParams.limit, transactions]);

  const getListRow = (transaction: Transaction) => (
    <List.Item key={transaction.transaction_id}>
      <Text as="p">{transaction.invoice_number || t('paddle.billing.transaction.invoice.not-applicable')}</Text>
      <Tag color={transaction.status === 'completed' ? 'green' : 'red'}>
        {t(transaction.status === 'completed' ? 'paddle.billing.transaction.payment-status.success' : 'paddle.billing.transaction.payment-status.failed')}
      </Tag>
      <Text as="p">{getTierName(transaction)}</Text>
      <Text as="p">
        {formatDate(new Date(transaction.billing_date), { format: 'LL LT' })}
      </Text>
      <Button
        text={t('paddle.billing.transaction.invoice-download.btn-text')}
        appearance="simple"
        icon="download"
        disabled={!transaction.invoice_number}
        onClick={async () => {
          try {
            productAnalytics.push({ type: PRODUCT_ANALYTICS_ACTION_TYPES.downloadInvoice });
            const invoiceUrl = await billingService.getInvoiceUrl(transaction.transaction_id);
            downloadURI(invoiceUrl, `${transaction.invoice_number}.pdf`);
            productAnalytics.push({ type: `${PRODUCT_ANALYTICS_ACTION_TYPES.downloadInvoice}/success` });
          } catch (err) {
            productAnalytics.push({ type: `${PRODUCT_ANALYTICS_ACTION_TYPES.downloadInvoice}/failed` });
          }
        }}
      />
    </List.Item>
  );

  const handleLimitChange = async (limit) => {
    const listPreference = new ListPreference(userPreferences.load(), '');
    listPreference.limit = limit;

    userPreferences.persist(listPreference);

    navigate({ to: buildPaginatedPath(pathname, 1, limit) });
  };

  const handlePageChange = async (offset) => {
    navigate({
      to: buildPaginatedPath(
        pathname,
        pagination.getPageFromOffset(offset),
        pagination.getLimit(),
      ),
    });
  };

  useEffect(() => {
    fetchTransactions();
    productAnalytics.push({ type: PRODUCT_ANALYTICS_ACTION_TYPES.showBillingAndPayments });
  }, []);

  if (isBillingLoading) {
    return <Spinner type="centered" className="x-large" />;
  }

  if (!isSubscriptionLoading && !hasPaddleSubscription) {
    return (
      <SplashScreen type="custom">
        <Stack vertical spacing="extraNarrow" verticalAlignment="center" horizontalAlignment="middle">
          <Stack.Item>
            <Text as="h1" size={48} weight={300} align="center" dataId="Title">
              {t('paddle.billing.no-subscription.title')}
            </Text>
          </Stack.Item>
          <Stack.Item>
            <Text as="p" size={18} align="center" dataId="Subtitle">
              {t('paddle.billing.no-subscription.description')}
            </Text>
          </Stack.Item>
        </Stack>
      </SplashScreen>
    );
  }

  if (failed) {
    return <SplashScreen type="internalServerError" />;
  }

  return (
    <Title part={t('platform.billing.title')}>
      <Layout maxWidth="wide">
        <View>
          <View.Header title={t('paddle.billing.view-header.title')} />
          <View.Body>
            <Stack vertical spacing="narrow">
              <Stack.Item fill>
                <ScheduledActionInformation />
              </Stack.Item>
              <Stack.Item fill>
                <StyledContainer
                  border={['left', 'top', 'right']}
                  padding="mid"
                  backgroundColor="gray"
                >
                  <PaymentBadge hidePlanName />
                </StyledContainer>
              </Stack.Item>
              <Stack.Item fill previousSpacing="none">
                <List
                  columns={[
                    {
                      size: 1,
                      name: t('paddle.billing.transactions-columns.invoice-number'),
                      align: 'left',
                    },
                    {
                      size: 1,
                      name: t('paddle.billing.transactions-columns.payment-status'),
                      align: 'left',
                    },
                    {
                      size: 1,
                      name: t('paddle.billing.transactions-columns.plan'),
                      align: 'left',
                    },
                    {
                      size: 2,
                      name: (
                        <List.SortableHeader
                          text={t('paddle.billing.transactions-columns.billing-date')}
                          order={dateSortOrder}
                          selected={dateSortOrder !== null}
                          onClick={() => setDateSortOrder(dateSortOrder === 'asc' ? 'dsc' : 'asc')}
                        />
                      ),
                      align: 'left',
                    },
                    {
                      size: 1,
                      name: '',
                      align: 'right',
                    },
                  ]}
                >
                  {transactionListFailed ? (
                    <List.Item background="gray">
                      <Stack vertical spacing="extraNarrow" horizontalAlignment="middle" fullWidth>
                        <Stack.Item previousSpacing="default" />
                        <Stack.Item>
                          <Icon name="exclamation-mark-circle" color="red" />
                        </Stack.Item>
                        <Stack.Item>
                          <Text color="Red" dataId="TextStyle">
                            <div style={{ marginBottom: '2px' }}>
                              {t('general-errors.inline-error.text')}
                            </div>
                          </Text>
                        </Stack.Item>
                        <Stack.Item previousSpacing="default" />
                      </Stack>
                    </List.Item>
                  ) : sortedAndSplittedTransactions?.map(getListRow)}
                </List>
              </Stack.Item>
              <Stack.Item fill>
                <Pagination
                  onLimitChange={handleLimitChange}
                  limit={pagination.getLimit()}
                  total={total}
                  limitOptions={config.get('PAGINATION_ITEMS_PER_PAGE_OPTIONS')}
                  startIndex={pagination.offset}
                  endIndex={pagination.offset + pagination.getLimit()}
                  onPageChange={handlePageChange}
                />
              </Stack.Item>
            </Stack>
          </View.Body>
        </View>
      </Layout>
    </Title>
  );
};

export default connector(withInject<InjectedProps>({
  billingService: 'MarketingCrate.billingService',
  Title: 'TitleCrate.components.Title',
  userPreferences: 'userPreferences',
  config: 'config',
  productAnalytics: 'productAnalytics',
  ScheduledActionInformation: 'MarketingCrate.components.ScheduledActionInformation',
})(withTranslation(['administration', 'platform'])(Billing as any)));
